import * as Styled from "./ProtectedRoute.styles";

import { AuthState, UserState } from "../../types/redux";
import { useEffect, useState } from "react";

import APIError from "../APIError/APIError";
import { CircularProgress } from "@mui/material";
import EnvironmentIndicator from "../EnvironmentIndicator/EnvironmentIndicator";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";
import LiveEventIndicator from "../LiveEventIndicator/LiveEventIndicator";
import { PageContainer } from "../PageContainer/PageContainer.styles";
import { getAllEvents } from "../../store/actions/eventsActions";
import { getAllUserEvents } from "../../store/actions/userEventsActions";
import { getUserInfo } from "../../store/actions/userActions";
import { useAppDispatch } from "../../store/hooks";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import dayjs from "dayjs";
import EventFeedbackFormModal from "../../modals/EventFeedbackModal/EventFeedbackModal";
import EventFeedbackSuccessModal from "../../modals/EventFeedbackSuccessModal/EventFeedbackSuccessModal";
import { checkIfFeedbackGiven } from "../../store/actions/liveEventActions";
import { Event } from "../../types/event";

interface ProtectedRouteProps {
  redirect?: string;
  children: any;
  showEnvironmentIndicator?: boolean;
  showFooter?: boolean;
  showHeader?: boolean;
  showLiveEventIndicator?: boolean;
}

const ProtectedRoute = ({
  children,
  showEnvironmentIndicator = process.env.REACT_APP_ENVIRONMENT !== "prod",
  showHeader = true,
  showFooter = true,
  showLiveEventIndicator = true,
}: ProtectedRouteProps) => {
  const authState: AuthState = useSelector((state: any) => state.auth);
  const userState: UserState = useSelector((state: any) => state.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {attendedEvents, feedbackGiven} = useSelector((state: RootState) => 
    ({
      attendedEvents: state.userEvents.attended,
      feedbackGiven: state.liveEvent.feedbackGiven
    }));

  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [showEventFeedbackModal, setShowEventFeedbackModal] = useState(false);
  const [latestEvent, setLatestEvent] = useState<Event | null>(null);
  const [showEventFeedbackSuccessModal, setShowEventFeedbackSuccessModal] = useState(false);

  useEffect(() => {
    if (!authState.isAuthorized) {
      navigate("/", { state: { from: location.pathname + location.search } });
    }
    dispatch(getUserInfo());
    dispatch(getAllEvents());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthorized]);

  useEffect(() => {
    if (!userState.loading && userState.success && userState.email !== "") {
      dispatch(getAllUserEvents(userState.email));
    }
  }, [userState.loading, userState.success, userState.email, dispatch]);

  useEffect(() => {
    if (
      (location.pathname.startsWith("/edit") ||
        location.pathname.startsWith("/manage") ||
        location.pathname.startsWith("/draft") ||
        location.pathname.startsWith("/create")) &&
      userState.role !== "ADMIN" &&
      userState.role !== "ADMIN:SUPER" &&
      authState.isAuthorized &&
      !userState.loading &&
      userState.success
    ) {
      navigate("/events", {
        state: {
          alert: {
            severity: "error",
            message: "You do not have access to this page.",
          },
        },
      });
    } else {
      setIsUserAdmin(true);
    }
  }, [
    location,
    userState.role,
    authState.isAuthorized,
    userState.loading,
    userState.success,
    navigate,
  ]);

  
  useEffect(() => {
    const fetchEvents = async () => {
      if(attendedEvents.length){
        const events = [...attendedEvents];
        const sorted = events.sort((a, b) => (b.endDateTime - a.endDateTime))
  
        if (!dayjs(sorted[0].endDateTime).add(1, "week").isAfter(dayjs())){
          await dispatch(checkIfFeedbackGiven({eventId: sorted[0].eventId, userId: userState.email}))
          setLatestEvent(sorted[0]);
        }
  
        const intervalId = setInterval( async () => {
          await dispatch(checkIfFeedbackGiven({eventId: sorted[0].eventId, userId: userState.email}))
        }, 60000);
  
        return () => {
            clearInterval(intervalId);
        };
      }
    }
    
    fetchEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendedEvents, dispatch, location]);

  useEffect(() => {
    if(latestEvent) {
      const currentTime = new Date();
      const endTime = new Date(latestEvent.endDateTime * 1000);
      const fiveMinutesBeforeEnd = new Date(
        endTime.getTime() - 5 * 60 * 1000
      );

      if(!feedbackGiven.loading){
        if(currentTime >= fiveMinutesBeforeEnd){
          if(feedbackGiven?.data?.eventFeedbackResponse?.rating !== undefined){
              setShowEventFeedbackModal(false);
            } else {
              setShowEventFeedbackModal(true);
            }
        }
      }
    }
  }, [feedbackGiven.data?.eventFeedbackResponse, latestEvent, feedbackGiven.data, feedbackGiven.loading])

  if (!isUserAdmin || authState.error) {
    return (
      <PageContainer>
        <Styled.LoaderContainer>
          <CircularProgress />
        </Styled.LoaderContainer>
      </PageContainer>
    );
  }

  if (userState.error) {
    return (
      <APIError
        onClick={() => {
          if (authState.isAuthorized) {
            dispatch(getUserInfo());
          }
        }}
      />
    );
  }

  return (
    <>
    <EventFeedbackFormModal
      open={showEventFeedbackModal}
      onClose={(event: Event, reason: string) => {
        if(reason !== "backdropClick"){
          setShowEventFeedbackModal(false);
        }
      }}
      onTransitionExited={(feedbackSubmitted: boolean) => {
        setShowEventFeedbackSuccessModal(feedbackSubmitted);
      }}
      eventData={{event: latestEvent}}
    />
    <EventFeedbackSuccessModal
      open={showEventFeedbackSuccessModal}
      onClose={() => {
        setShowEventFeedbackSuccessModal(false);
        navigate("/events")
      }}
    />
      {!userState.loading &&
      userState.success &&
      !userState.error &&
      authState.isAuthorized ? (
        <>
          {showHeader && (
            <div id="header-container">
              <Header />
            </div>
          )}
          {showLiveEventIndicator && <LiveEventIndicator />}
          {children}
          {showEnvironmentIndicator && <EnvironmentIndicator />}
          {showFooter && <Footer />}
        </>
      ) : (
        <PageContainer>
          <Styled.LoaderContainer>
            <CircularProgress />
          </Styled.LoaderContainer>
        </PageContainer>
      )}
    </>
  );
};

export default ProtectedRoute;
